import { useState } from 'react';
import { motion } from 'framer-motion';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import {
  Calendar, Clock, Bell, Repeat, Check, Lock, CalendarDays,
  Copy, Share2, MessageCircle, Mail, Send, Printer, Download,
  Link2, ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useUserCredits } from '@/hooks/useUserCredits';
import { canUseScheduling, canUseRecurring } from '@/types/credits';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const schCopy = {
  en: {
    title: 'Publish & Share',
    subtitle: 'Choose when and how your message reaches them.',
    schedulingGate: 'Scheduling is a Connection feature',
    schedulingGateDesc: 'Choose Connection or Legacy to schedule announcements for the perfect moment.',
    unlock: 'Unlock more support →',
    scheduleLater: 'Schedule for later',
    chooseFuture: 'Choose a future date and time',
    selectDate: 'Select Date',
    pickDate: 'Pick a date',
    selectTime: 'Select Time',
    chooseTime: 'Choose a time',
    emailReminder: 'Email reminder',
    reminderDesc: "We'll gently remind you before this message goes out.",
    recurringAnnually: 'Recurring annually',
    recurringDesc: "Set this once. We'll remember every year.",
    confirmTitle: "We'll send this when you asked — you can change or cancel this anytime.",
    scheduledFor: 'Scheduled for',
    at: 'at',
    repeatsAnnually: '(repeats annually)',
    controlNote: 'Nothing is sent until the scheduled time. You remain in control.',
    publishNow: 'Your announcement will be published immediately when you click "Publish"',
    shareTitle: 'How would you like to deliver your message?',
    shareDesc: 'Choose how you want to share — then publish.',
    shareReady: 'Your message is published! Share it now.',
    copyLink: 'Copy link',
    linkCopied: 'Link copied!',
    sendEmail: 'Send via email',
    emailPlaceholder: 'Recipient email address',
    emailSent: 'Email sent!',
    emailFailed: 'Failed to send email',
    sendText: 'Send as text',
    shareOnSocial: 'Share on social',
    printLabel: 'Print / Download',
    print: 'Print',
    savePdf: 'Save as PDF',
    nativeShare: 'Share',
    noAutoPost: 'Nothing is posted automatically. You control who sees this.',
    invalidEmail: 'Please enter a valid email address',
    printGated: 'Print is available for Connection and Legacy plans',
    pdfGated: 'PDF download is available for Connection and Legacy plans',
    pdfHint: 'Print dialog opened — choose "Save as PDF" to download',
    keepsakeApplied: 'Keepsake-quality formatting applied',
    shareOn: 'Share on',
    selectShareMethod: 'Select a sharing method to continue',
    copyLinkDesc: 'Get a shareable link to send however you\'d like',
    emailDesc: 'We\'ll deliver it directly to their inbox',
    smsDesc: 'Open your messaging app with the link ready',
  },
  es: {
    title: 'Publicar y Compartir',
    subtitle: 'Elige cuándo y cómo tu mensaje les llega.',
    schedulingGate: 'La programación es una función de Connection',
    schedulingGateDesc: 'Elige Connection o Legacy para programar anuncios en el momento perfecto.',
    unlock: 'Desbloquear más soporte →',
    scheduleLater: 'Programar para después',
    chooseFuture: 'Elige una fecha y hora futuras',
    selectDate: 'Seleccionar Fecha',
    pickDate: 'Elegir fecha',
    selectTime: 'Seleccionar Hora',
    chooseTime: 'Elegir hora',
    emailReminder: 'Recordatorio por email',
    reminderDesc: 'Te recordaremos antes de que se envíe este mensaje.',
    recurringAnnually: 'Repetir anualmente',
    recurringDesc: 'Configúralo una vez. Lo recordaremos cada año.',
    confirmTitle: 'Lo enviaremos cuando pediste — puedes cambiarlo en cualquier momento.',
    scheduledFor: 'Programado para',
    at: 'a las',
    repeatsAnnually: '(se repite anualmente)',
    controlNote: 'Nada se envía hasta la hora programada. Tú tienes el control.',
    publishNow: 'Tu anuncio se publicará inmediatamente al hacer clic en "Publicar"',
    shareTitle: '¿Cómo te gustaría entregar tu mensaje?',
    shareDesc: 'Elige cómo quieres compartir — luego publica.',
    shareReady: '¡Tu mensaje está publicado! Compártelo ahora.',
    copyLink: 'Copiar enlace',
    linkCopied: '¡Enlace copiado!',
    sendEmail: 'Enviar por email',
    emailPlaceholder: 'Email del destinatario',
    emailSent: '¡Email enviado!',
    emailFailed: 'Error al enviar email',
    sendText: 'Enviar como texto',
    shareOnSocial: 'Compartir en redes',
    printLabel: 'Imprimir / Descargar',
    print: 'Imprimir',
    savePdf: 'Guardar como PDF',
    nativeShare: 'Compartir',
    noAutoPost: 'Nada se publica automáticamente. Tú controlas quién ve esto.',
    invalidEmail: 'Por favor ingresa un email válido',
    printGated: 'Imprimir está disponible para los planes Connection y Legacy',
    pdfGated: 'La descarga de PDF está disponible para los planes Connection y Legacy',
    pdfHint: 'Diálogo de impresión abierto — elige "Guardar como PDF"',
    keepsakeApplied: 'Formato keepsake aplicado',
    shareOn: 'Compartir en',
    selectShareMethod: 'Selecciona un método de compartir para continuar',
    copyLinkDesc: 'Obtén un enlace para compartir como prefieras',
    emailDesc: 'Lo entregaremos directamente a su bandeja de entrada',
    smsDesc: 'Abre tu app de mensajes con el enlace listo',
  },
  fr: {
    title: 'Publier et Partager',
    subtitle: 'Choisissez quand et comment votre message les atteint.',
    schedulingGate: 'La programmation est une fonctionnalité Connection',
    schedulingGateDesc: 'Choisissez Connection ou Legacy pour programmer au moment parfait.',
    unlock: 'Débloquer plus →',
    scheduleLater: 'Programmer pour plus tard',
    chooseFuture: 'Choisissez une date et heure futures',
    selectDate: 'Sélectionner la Date',
    pickDate: 'Choisir une date',
    selectTime: "Sélectionner l'Heure",
    chooseTime: 'Choisir une heure',
    emailReminder: 'Rappel par email',
    reminderDesc: "Nous vous rappellerons avant l'envoi.",
    recurringAnnually: 'Récurrence annuelle',
    recurringDesc: 'Configurez une fois. Nous nous en souviendrons chaque année.',
    confirmTitle: "Nous l'enverrons quand vous l'avez demandé — vous pouvez modifier à tout moment.",
    scheduledFor: 'Programmé pour le',
    at: 'à',
    repeatsAnnually: '(répète annuellement)',
    controlNote: "Rien n'est envoyé avant l'heure programmée. Vous gardez le contrôle.",
    publishNow: 'Votre annonce sera publiée immédiatement en cliquant sur "Publier"',
    shareTitle: 'Comment souhaitez-vous transmettre votre message ?',
    shareDesc: 'Choisissez comment partager — puis publiez.',
    shareReady: 'Votre message est publié ! Partagez-le maintenant.',
    copyLink: 'Copier le lien',
    linkCopied: 'Lien copié !',
    sendEmail: 'Envoyer par email',
    emailPlaceholder: "Adresse email du destinataire",
    emailSent: 'Email envoyé !',
    emailFailed: "Échec de l'envoi",
    sendText: 'Envoyer par SMS',
    shareOnSocial: 'Partager sur les réseaux',
    printLabel: 'Imprimer / Télécharger',
    print: 'Imprimer',
    savePdf: 'Sauvegarder en PDF',
    nativeShare: 'Partager',
    noAutoPost: "Rien n'est partagé automatiquement. Vous contrôlez qui voit ceci.",
    invalidEmail: 'Veuillez entrer un email valide',
    printGated: 'Imprimer est disponible pour les plans Connection et Legacy',
    pdfGated: 'Le téléchargement PDF est disponible pour les plans Connection et Legacy',
    pdfHint: 'Dialogue d\'impression ouvert — choisissez "Sauvegarder en PDF"',
    keepsakeApplied: 'Format keepsake appliqué',
    shareOn: 'Partager sur',
    selectShareMethod: 'Sélectionnez une méthode de partage pour continuer',
    copyLinkDesc: 'Obtenez un lien partageable à envoyer comme vous le souhaitez',
    emailDesc: 'Nous le livrerons directement dans leur boîte de réception',
    smsDesc: 'Ouvrez votre application de messagerie avec le lien prêt',
  },
  pt: {
    title: 'Publicar e Compartilhar',
    subtitle: 'Escolha quando e como sua mensagem chega até eles.',
    schedulingGate: 'Agendamento é uma função do Connection',
    schedulingGateDesc: 'Escolha Connection ou Legacy para agendar no momento perfeito.',
    unlock: 'Desbloquear mais →',
    scheduleLater: 'Agendar para depois',
    chooseFuture: 'Escolha uma data e hora futuras',
    selectDate: 'Selecionar Data',
    pickDate: 'Escolher data',
    selectTime: 'Selecionar Hora',
    chooseTime: 'Escolher hora',
    emailReminder: 'Lembrete por email',
    reminderDesc: 'Vamos lembrá-lo antes do envio.',
    recurringAnnually: 'Repetir anualmente',
    recurringDesc: 'Configure uma vez. Lembraremos todo ano.',
    confirmTitle: 'Enviaremos quando pediu — pode alterar a qualquer momento.',
    scheduledFor: 'Agendado para',
    at: 'às',
    repeatsAnnually: '(repete anualmente)',
    controlNote: 'Nada é enviado até o horário agendado. Você mantém o controle.',
    publishNow: 'Seu anúncio será publicado imediatamente ao clicar em "Publicar"',
    shareTitle: 'Como gostaria de entregar sua mensagem?',
    shareDesc: 'Escolha como compartilhar — depois publique.',
    shareReady: 'Sua mensagem foi publicada! Compartilhe agora.',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado!',
    sendEmail: 'Enviar por email',
    emailPlaceholder: 'Email do destinatário',
    emailSent: 'Email enviado!',
    emailFailed: 'Falha ao enviar email',
    sendText: 'Enviar como texto',
    shareOnSocial: 'Compartilhar nas redes',
    printLabel: 'Imprimir / Baixar',
    print: 'Imprimir',
    savePdf: 'Salvar como PDF',
    nativeShare: 'Compartilhar',
    noAutoPost: 'Nada é compartilhado automaticamente. Você controla quem vê isso.',
    invalidEmail: 'Por favor insira um email válido',
    printGated: 'Imprimir está disponível para os planos Connection e Legacy',
    pdfGated: 'Download de PDF está disponível para os planos Connection e Legacy',
    pdfHint: 'Diálogo de impressão aberto — escolha "Salvar como PDF"',
    keepsakeApplied: 'Formato keepsake aplicado',
    shareOn: 'Compartilhar no',
    selectShareMethod: 'Selecione um método de compartilhamento para continuar',
    copyLinkDesc: 'Obtenha um link compartilhável para enviar como preferir',
    emailDesc: 'Entregaremos diretamente na caixa de entrada',
    smsDesc: 'Abra seu app de mensagens com o link pronto',
  },
  ar: {
    title: 'نشر ومشاركة',
    subtitle: 'اختر متى وكيف تصل رسالتك إليهم.',
    schedulingGate: 'الجدولة ميزة في Connection',
    schedulingGateDesc: 'اختر Connection أو Legacy لجدولة الإعلانات.',
    unlock: 'فتح المزيد →',
    scheduleLater: 'جدولة لاحقاً',
    chooseFuture: 'اختر تاريخاً ووقتاً مستقبلياً',
    selectDate: 'اختيار التاريخ',
    pickDate: 'اختر تاريخاً',
    selectTime: 'اختيار الوقت',
    chooseTime: 'اختر وقتاً',
    emailReminder: 'تذكير بالبريد',
    reminderDesc: 'سنذكرك قبل إرسال الرسالة.',
    recurringAnnually: 'تكرار سنوي',
    recurringDesc: 'اضبطه مرة واحدة وسنتذكره كل عام.',
    confirmTitle: 'سنرسله عندما طلبت — يمكنك تغييره في أي وقت.',
    scheduledFor: 'مجدول لـ',
    at: 'في',
    repeatsAnnually: '(يتكرر سنوياً)',
    controlNote: 'لا شيء يُرسل حتى الموعد المحدد. أنت المتحكم.',
    publishNow: 'سيتم نشر إعلانك فوراً عند النقر على "نشر"',
    shareTitle: 'كيف تريد توصيل رسالتك؟',
    shareDesc: 'اختر كيف تريد المشاركة — ثم انشر.',
    shareReady: 'رسالتك منشورة! شاركها الآن.',
    copyLink: 'نسخ الرابط',
    linkCopied: 'تم نسخ الرابط!',
    sendEmail: 'إرسال بالبريد',
    emailPlaceholder: 'بريد المستلم',
    emailSent: 'تم إرسال البريد!',
    emailFailed: 'فشل إرسال البريد',
    sendText: 'إرسال كرسالة نصية',
    shareOnSocial: 'مشاركة على وسائل التواصل',
    printLabel: 'طباعة / تنزيل',
    print: 'طباعة',
    savePdf: 'حفظ كـ PDF',
    nativeShare: 'مشاركة',
    noAutoPost: 'لا شيء يُشارك تلقائياً. أنت تتحكم في من يرى هذا.',
    invalidEmail: 'يرجى إدخال بريد إلكتروني صالح',
    printGated: 'الطباعة متاحة لخطط Connection و Legacy',
    pdfGated: 'تنزيل PDF متاح لخطط Connection و Legacy',
    pdfHint: 'تم فتح نافذة الطباعة — اختر "حفظ كـ PDF"',
    keepsakeApplied: 'تم تطبيق تنسيق keepsake',
    shareOn: 'مشاركة على',
    selectShareMethod: 'اختر طريقة مشاركة للمتابعة',
    copyLinkDesc: 'رابط قابل للمشاركة لإرساله كما تشاء',
    emailDesc: 'سنوصله مباشرة إلى صندوق بريدهم',
    smsDesc: 'افتح تطبيق الرسائل مع الرابط جاهزاً',
  },
  ja: {
    title: '公開して共有',
    subtitle: 'いつ、どのようにメッセージを届けるか選びましょう。',
    schedulingGate: 'スケジュールはConnectionの機能です',
    schedulingGateDesc: 'ConnectionまたはLegacyでスケジュール機能をご利用ください。',
    unlock: 'もっと機能を解放 →',
    scheduleLater: '後でスケジュール',
    chooseFuture: '将来の日時を選択',
    selectDate: '日付を選択',
    pickDate: '日付を選ぶ',
    selectTime: '時間を選択',
    chooseTime: '時間を選ぶ',
    emailReminder: 'メールリマインダー',
    reminderDesc: '送信前にやさしくお知らせします。',
    recurringAnnually: '毎年繰り返し',
    recurringDesc: '一度設定すれば毎年覚えています。',
    confirmTitle: 'ご指定の時間に送信します — いつでも変更できます。',
    scheduledFor: '予定日',
    at: '',
    repeatsAnnually: '（毎年繰り返し）',
    controlNote: '予定時間まで何も送信されません。あなたが管理しています。',
    publishNow: '「公開」をクリックするとすぐに公開されます',
    shareTitle: 'どのようにメッセージを届けますか？',
    shareDesc: '共有方法を選んでから公開しましょう。',
    shareReady: 'メッセージが公開されました！今すぐ共有しましょう。',
    copyLink: 'リンクをコピー',
    linkCopied: 'リンクがコピーされました！',
    sendEmail: 'メールで送信',
    emailPlaceholder: '受信者のメールアドレス',
    emailSent: 'メール送信完了！',
    emailFailed: 'メール送信に失敗',
    sendText: 'テキストで送信',
    shareOnSocial: 'ソーシャルで共有',
    printLabel: '印刷 / ダウンロード',
    print: '印刷',
    savePdf: 'PDFで保存',
    nativeShare: '共有',
    noAutoPost: '自動的に共有されることはありません。誰が見るかはあなたが決めます。',
    invalidEmail: '有効なメールアドレスを入力してください',
    printGated: '印刷はConnectionとLegacyプランで利用可能です',
    pdfGated: 'PDFダウンロードはConnectionとLegacyプランで利用可能です',
    pdfHint: '印刷ダイアログが開きました — 「PDFとして保存」を選択してください',
    keepsakeApplied: 'キープセイク品質のフォーマットが適用されました',
    shareOn: 'で共有',
    selectShareMethod: '共有方法を選択してから続行してください',
    copyLinkDesc: '好きな方法で送れる共有リンクを取得',
    emailDesc: '受信者の受信箱に直接お届けします',
    smsDesc: 'リンク付きでメッセージアプリを開きます',
  },
  ko: {
    title: '게시 및 공유',
    subtitle: '언제, 어떻게 메시지를 전달할지 선택하세요.',
    schedulingGate: '예약은 Connection 기능입니다',
    schedulingGateDesc: 'Connection 또는 Legacy를 선택하여 완벽한 순간에 예약하세요.',
    unlock: '더 많은 기능 해제 →',
    scheduleLater: '나중에 예약',
    chooseFuture: '미래 날짜와 시간을 선택하세요',
    selectDate: '날짜 선택',
    pickDate: '날짜 선택',
    selectTime: '시간 선택',
    chooseTime: '시간 선택',
    emailReminder: '이메일 알림',
    reminderDesc: '발송 전에 부드럽게 알려드립니다.',
    recurringAnnually: '매년 반복',
    recurringDesc: '한 번 설정하면 매년 기억합니다.',
    confirmTitle: '요청하신 시간에 보내드립니다 — 언제든 변경할 수 있습니다.',
    scheduledFor: '예약일',
    at: '',
    repeatsAnnually: '(매년 반복)',
    controlNote: '예약 시간까지 아무것도 전송되지 않습니다. 당신이 관리합니다.',
    publishNow: '"게시"를 클릭하면 바로 게시됩니다',
    shareTitle: '어떻게 메시지를 전달하시겠습니까?',
    shareDesc: '공유 방법을 선택한 후 게시하세요.',
    shareReady: '메시지가 게시되었습니다! 지금 공유하세요.',
    copyLink: '링크 복사',
    linkCopied: '링크가 복사되었습니다!',
    sendEmail: '이메일로 보내기',
    emailPlaceholder: '수신자 이메일 주소',
    emailSent: '이메일 전송 완료!',
    emailFailed: '이메일 전송 실패',
    sendText: '문자로 보내기',
    shareOnSocial: '소셜에서 공유',
    printLabel: '인쇄 / 다운로드',
    print: '인쇄',
    savePdf: 'PDF로 저장',
    nativeShare: '공유',
    noAutoPost: '자동으로 공유되지 않습니다. 누가 볼지는 당신이 결정합니다.',
    invalidEmail: '유효한 이메일 주소를 입력하세요',
    printGated: '인쇄는 Connection 및 Legacy 플랜에서 사용 가능합니다',
    pdfGated: 'PDF 다운로드는 Connection 및 Legacy 플랜에서 사용 가능합니다',
    pdfHint: '인쇄 대화상자가 열렸습니다 — "PDF로 저장"을 선택하세요',
    keepsakeApplied: '키프세이크 품질 포맷 적용됨',
    shareOn: '에서 공유',
    selectShareMethod: '공유 방법을 선택하여 계속하세요',
    copyLinkDesc: '원하는 방식으로 보낼 수 있는 공유 링크를 받으세요',
    emailDesc: '수신자의 받은편지함으로 직접 전달합니다',
    smsDesc: '링크가 준비된 메시지 앱을 엽니다',
  },
  id: {
    title: 'Terbitkan & Bagikan',
    subtitle: 'Pilih kapan dan bagaimana pesan Anda sampai kepada mereka.',
    schedulingGate: 'Penjadwalan adalah fitur Connection',
    schedulingGateDesc: 'Pilih Connection atau Legacy untuk menjadwalkan pengumuman.',
    unlock: 'Buka lebih banyak →',
    scheduleLater: 'Jadwalkan nanti',
    chooseFuture: 'Pilih tanggal dan waktu di masa depan',
    selectDate: 'Pilih Tanggal',
    pickDate: 'Pilih tanggal',
    selectTime: 'Pilih Waktu',
    chooseTime: 'Pilih waktu',
    emailReminder: 'Pengingat email',
    reminderDesc: 'Kami akan mengingatkan sebelum pesan dikirim.',
    recurringAnnually: 'Berulang tahunan',
    recurringDesc: 'Atur sekali. Kami akan ingat setiap tahun.',
    confirmTitle: 'Kami akan mengirimnya sesuai permintaan Anda — bisa diubah kapan saja.',
    scheduledFor: 'Dijadwalkan pada',
    at: 'pukul',
    repeatsAnnually: '(berulang tahunan)',
    controlNote: 'Tidak ada yang dikirim hingga waktu terjadwal. Anda tetap mengendalikan.',
    publishNow: 'Pengumuman Anda akan diterbitkan segera saat Anda klik "Terbitkan"',
    shareTitle: 'Bagaimana Anda ingin menyampaikan pesan Anda?',
    shareDesc: 'Pilih cara berbagi — lalu terbitkan.',
    shareReady: 'Pesan Anda sudah diterbitkan! Bagikan sekarang.',
    copyLink: 'Salin tautan',
    linkCopied: 'Tautan disalin!',
    sendEmail: 'Kirim via email',
    emailPlaceholder: 'Alamat email penerima',
    emailSent: 'Email terkirim!',
    emailFailed: 'Gagal mengirim email',
    sendText: 'Kirim sebagai teks',
    shareOnSocial: 'Bagikan di sosial',
    printLabel: 'Cetak / Unduh',
    print: 'Cetak',
    savePdf: 'Simpan sebagai PDF',
    nativeShare: 'Bagikan',
    noAutoPost: 'Tidak ada yang dibagikan secara otomatis. Anda menentukan siapa yang melihat ini.',
    invalidEmail: 'Silakan masukkan alamat email yang valid',
    printGated: 'Cetak tersedia untuk paket Connection dan Legacy',
    pdfGated: 'Unduh PDF tersedia untuk paket Connection dan Legacy',
    pdfHint: 'Dialog cetak dibuka — pilih "Simpan sebagai PDF"',
    keepsakeApplied: 'Format keepsake diterapkan',
    shareOn: 'Bagikan di',
    selectShareMethod: 'Pilih metode berbagi untuk melanjutkan',
    copyLinkDesc: 'Dapatkan tautan yang bisa dibagikan sesuka Anda',
    emailDesc: 'Kami akan mengirimkannya langsung ke kotak masuk mereka',
    smsDesc: 'Buka aplikasi pesan Anda dengan tautan siap',
  },
};

export type ShareMethod = 'copy_link' | 'email' | 'sms' | 'facebook' | 'x' | 'whatsapp' | null;

export interface ScheduleData {
  isScheduled: boolean;
  scheduledDate: Date | null;
  scheduledTime: string;
  enableReminder: boolean;
  isRecurring: boolean;
  recurringType: 'annual' | null;
}

interface ScheduleStepProps {
  scheduleData: ScheduleData;
  updateScheduleData: (updates: Partial<ScheduleData>) => void;
  publishedAnnouncement?: { public_slug: string; recipient_name: string; id?: string; template?: string; relationship?: string; message?: string; photo_urls?: string[] | null; published_at?: string | null; created_at?: string } | null;
  announcementId?: string;
  selectedShareMethod: ShareMethod;
  onShareMethodChange: (method: ShareMethod) => void;
  recipientEmail: string;
  onRecipientEmailChange: (email: string) => void;
}

const timeOptions = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
];

// Social platform icons
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function ScheduleStep({
  scheduleData,
  updateScheduleData,
  selectedShareMethod,
  onShareMethodChange,
  recipientEmail,
  onRecipientEmailChange,
}: ScheduleStepProps) {
  const { language } = useLanguage();
  const c = lc(schCopy, language);
  const { credits } = useUserCredits();
  const highestBundle = credits?.highest_bundle_purchased || null;
  const hasSchedulingAccess = canUseScheduling(highestBundle);
  const hasRecurringAccess = canUseRecurring(highestBundle);
  const minDate = startOfDay(addDays(new Date(), 1));

  const handleDateSelect = (date: Date | undefined) => {
    if (date && !isBefore(date, minDate)) updateScheduleData({ scheduledDate: date });
  };
  const handleTimeChange = (time: string) => updateScheduleData({ scheduledTime: time });
  const toggleScheduled = () => {
    const newScheduled = !scheduleData.isScheduled;
    updateScheduleData({ isScheduled: newScheduled, ...(newScheduled ? {} : { scheduledDate: null, scheduledTime: '09:00', enableReminder: false, isRecurring: false, recurringType: null }) });
  };
  const toggleReminder = () => updateScheduleData({ enableReminder: !scheduleData.enableReminder });
  const toggleRecurring = () => {
    const newRecurring = !scheduleData.isRecurring;
    updateScheduleData({ isRecurring: newRecurring, recurringType: newRecurring ? 'annual' : null });
  };

  const selectMethod = (method: ShareMethod) => {
    onShareMethodChange(selectedShareMethod === method ? null : method);
  };

  const platforms: { key: 'facebook' | 'x' | 'whatsapp'; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { key: 'facebook', label: 'Facebook', Icon: FacebookIcon },
    { key: 'x', label: 'X', Icon: XIcon },
    { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon },
  ];

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Send className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{c.title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">{c.subtitle}</p>
      </div>

      <div className="max-w-lg mx-auto space-y-8">

        {/* ─── SECTION 1: SCHEDULING ─── */}
        <div className="space-y-4">
          {!hasSchedulingAccess ? (
            <div className="bg-muted/50 border border-border rounded-xl p-6 text-center">
              <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-medium text-foreground mb-2">{c.schedulingGate}</h3>
              <p className="text-sm text-muted-foreground mb-4">{c.schedulingGateDesc}</p>
              <Badge variant="outline">{c.unlock}</Badge>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10"><Calendar className="h-5 w-5 text-primary" /></div>
                  <div>
                    <Label htmlFor="schedule-toggle" className="font-medium">{c.scheduleLater}</Label>
                    <p className="text-sm text-muted-foreground">{c.chooseFuture}</p>
                  </div>
                </div>
                <Switch id="schedule-toggle" checked={scheduleData.isScheduled} onCheckedChange={toggleScheduled} />
              </div>

              {scheduleData.isScheduled && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-4">
                  <div className="p-4 rounded-xl border border-border bg-card space-y-4">
                    <Label className="font-medium flex items-center gap-2"><CalendarDays className="h-4 w-4" />{c.selectDate}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !scheduleData.scheduledDate && "text-muted-foreground")}>
                          <Calendar className="mr-2 h-4 w-4" />
                          {scheduleData.scheduledDate ? format(scheduleData.scheduledDate, 'EEEE, MMMM d, yyyy') : c.pickDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent mode="single" selected={scheduleData.scheduledDate || undefined} onSelect={handleDateSelect} disabled={(date) => isBefore(date, minDate)} initialFocus className={cn("p-3 pointer-events-auto")} />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="p-4 rounded-xl border border-border bg-card space-y-4">
                    <Label className="font-medium flex items-center gap-2"><Clock className="h-4 w-4" />{c.selectTime}</Label>
                    <Select value={scheduleData.scheduledTime} onValueChange={handleTimeChange}>
                      <SelectTrigger className="w-full"><SelectValue placeholder={c.chooseTime} /></SelectTrigger>
                      <SelectContent>
                        {timeOptions.map((time) => (
                          <SelectItem key={time} value={time}>{format(new Date(`2000-01-01T${time}`), 'h:mm a')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-secondary/10"><Bell className="h-5 w-5 text-secondary" /></div>
                      <div>
                        <Label htmlFor="reminder-toggle" className="font-medium">{c.emailReminder}</Label>
                        <p className="text-sm text-muted-foreground">{c.reminderDesc}</p>
                      </div>
                    </div>
                    <Switch id="reminder-toggle" checked={scheduleData.enableReminder} onCheckedChange={toggleReminder} />
                  </div>

                  <div className={cn("flex items-center justify-between p-4 rounded-xl border bg-card", hasRecurringAccess ? "border-amber-200 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20" : "border-border opacity-60")}>
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", hasRecurringAccess ? "bg-amber-500/10" : "bg-muted")}>
                        <Repeat className={cn("h-5 w-5", hasRecurringAccess ? "text-amber-600" : "text-muted-foreground")} />
                      </div>
                      <div>
                        <Label htmlFor="recurring-toggle" className="font-medium flex items-center gap-2">
                          {c.recurringAnnually}
                          {!hasRecurringAccess && <Badge variant="outline" className="text-xs">Legacy</Badge>}
                        </Label>
                        <p className="text-sm text-muted-foreground">{c.recurringDesc}</p>
                      </div>
                    </div>
                    <Switch id="recurring-toggle" checked={scheduleData.isRecurring} onCheckedChange={toggleRecurring} disabled={!hasRecurringAccess} />
                  </div>

                  {scheduleData.scheduledDate && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-full bg-primary/10"><Check className="h-4 w-4 text-primary" /></div>
                        <div>
                          <p className="font-medium text-foreground">{c.confirmTitle}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {c.scheduledFor} {format(scheduleData.scheduledDate, 'EEEE, MMMM d, yyyy')} {c.at}{' '}
                            {format(new Date(`2000-01-01T${scheduleData.scheduledTime}`), 'h:mm a')}
                            {scheduleData.isRecurring && ` ${c.repeatsAnnually}`}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2 italic">{c.controlNote}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </>
          )}

          {(!scheduleData.isScheduled || !hasSchedulingAccess) && (
            <div className="text-center p-4 rounded-xl border border-dashed border-border">
              <p className="text-sm text-muted-foreground">{c.publishNow}</p>
            </div>
          )}
        </div>

        <Separator />

        {/* ─── SECTION 2: SHARE OPTIONS (selectable before publish) ─── */}
        <div className="space-y-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary mb-3">
              <Share2 className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">{c.shareTitle}</h3>
            <p className="text-sm text-muted-foreground mt-1">{c.shareDesc}</p>
          </div>

          {/* Copy link */}
          <button
            type="button"
            onClick={() => selectMethod('copy_link')}
            className={cn(
              "w-full p-4 rounded-xl border bg-card text-left transition-all",
              selectedShareMethod === 'copy_link'
                ? "border-primary ring-2 ring-primary/20"
                : "border-border hover:border-primary/40"
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", selectedShareMethod === 'copy_link' ? "bg-primary/20" : "bg-primary/10")}>
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <span className="font-medium text-foreground">{c.copyLink}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{c.copyLinkDesc}</p>
              </div>
              {selectedShareMethod === 'copy_link' && (
                <Check className="h-5 w-5 text-primary ml-auto" />
              )}
            </div>
          </button>

          {/* Email */}
          <div
            className={cn(
              "p-4 rounded-xl border bg-card transition-all",
              selectedShareMethod === 'email'
                ? "border-primary ring-2 ring-primary/20"
                : "border-border hover:border-primary/40"
            )}
          >
            <button
              type="button"
              onClick={() => selectMethod('email')}
              className="w-full text-left"
            >
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", selectedShareMethod === 'email' ? "bg-primary/20" : "bg-primary/10")}>
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <span className="font-medium text-foreground">{c.sendEmail}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.emailDesc}</p>
                </div>
                {selectedShareMethod === 'email' && (
                  <Check className="h-5 w-5 text-primary ml-auto" />
                )}
              </div>
            </button>
            {selectedShareMethod === 'email' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 pt-3 border-t border-border"
              >
                <Input
                  type="email"
                  placeholder={c.emailPlaceholder}
                  value={recipientEmail}
                  onChange={(e) => onRecipientEmailChange(e.target.value)}
                  className="w-full"
                  autoFocus
                />
                {recipientEmail && !isEmailValid && (
                  <p className="text-xs text-destructive mt-1">{c.invalidEmail}</p>
                )}
              </motion.div>
            )}
          </div>

          {/* SMS */}
          <button
            type="button"
            onClick={() => selectMethod('sms')}
            className={cn(
              "w-full p-4 rounded-xl border bg-card text-left transition-all",
              selectedShareMethod === 'sms'
                ? "border-primary ring-2 ring-primary/20"
                : "border-border hover:border-primary/40"
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", selectedShareMethod === 'sms' ? "bg-primary/20" : "bg-primary/10")}>
                <MessageCircle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <span className="font-medium text-foreground">{c.sendText}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{c.smsDesc}</p>
              </div>
              {selectedShareMethod === 'sms' && (
                <Check className="h-5 w-5 text-primary ml-auto" />
              )}
            </div>
          </button>

          {/* Social */}
          <div className="p-4 rounded-xl border border-border bg-card space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><Share2 className="h-5 w-5 text-primary" /></div>
              <span className="font-medium text-foreground">{c.shareOnSocial}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {platforms.map(({ key, label, Icon }) => (
                <Button
                  key={key}
                  variant="outline"
                  className={cn(
                    "gap-2 justify-center transition-all",
                    selectedShareMethod === key && "border-primary ring-2 ring-primary/20 bg-primary/5"
                  )}
                  onClick={() => selectMethod(key)}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Hint when no method selected */}
          {!selectedShareMethod && (
            <div className="text-center p-3 rounded-xl border border-dashed border-primary/30 bg-primary/5">
              <p className="text-sm text-primary/80">{c.selectShareMethod}</p>
            </div>
          )}

          {/* Footer reassurance */}
          <div className="text-center pt-2">
            <p className="text-xs text-muted-foreground">{c.noAutoPost}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export const INITIAL_SCHEDULE_DATA: ScheduleData = {
  isScheduled: false,
  scheduledDate: null,
  scheduledTime: '09:00',
  enableReminder: false,
  isRecurring: false,
  recurringType: null,
};
